import React, { useEffect, useState } from "react";

/**
 * Time picker with a 12H / 24H toggle.
 *
 * `value` is always stored as canonical 24-hour "HH:MM" so the payload
 * shape (and backend) never changes based on the UI mode.
 *
 * The user can toggle between:
 *   - 12H mode → HH(1..12) / MM / AM-PM
 *   - 24H mode → HH(0..23) / MM
 */
const HOUR_KEY_STORAGE = "astrodev_time_fmt"; // '12' | '24'

function parseValue(v) {
  if (!v) return { h: "", m: "" };
  const [hStr = "", mStr = ""] = v.split(":");
  const h = parseInt(hStr, 10);
  const mn = parseInt(mStr, 10);
  if (Number.isNaN(h) || Number.isNaN(mn)) return { h: "", m: "" };
  return { h, m: String(mn).padStart(2, "0") };
}

// Build 24H canonical HH:MM given 12H parts
function build12(h12, m, ap) {
  if (!h12 || !m) return "";
  let h = parseInt(h12, 10) % 12;
  if (ap === "PM") h += 12;
  return `${String(h).padStart(2, "0")}:${m}`;
}

// Build 24H canonical from raw 24H parts
function build24(h24, m) {
  if (h24 === "" || !m) return "";
  return `${String(parseInt(h24, 10)).padStart(2, "0")}:${m}`;
}

export default function TimePicker({
  value,
  onChange,
  testId = "time-picker",
  required = false,
  // Optional external control of the 12H/24H format. When provided, the
  // in-component format toggle is hidden and the parent owns the choice.
  format: externalFormat,
  showToggle = true,
}) {
  // format preference persists per browser
  const [internalFmt, setInternalFmt] = useState(() => {
    if (typeof window === "undefined") return "12";
    return localStorage.getItem(HOUR_KEY_STORAGE) === "24" ? "24" : "12";
  });
  const fmt = externalFormat || internalFmt;
  const setFmt = (m) => {
    setInternalFmt(m);
  };
  useEffect(() => {
    if (!externalFormat) localStorage.setItem(HOUR_KEY_STORAGE, internalFmt);
  }, [internalFmt, externalFormat]);

  // internal parts held per-mode so partial selections are preserved
  const parsed = parseValue(value);
  const initialAp = parsed.h !== "" && parsed.h >= 12 ? "PM" : "AM";
  const initialH12 = (() => {
    if (parsed.h === "") return "";
    let h12 = parsed.h % 12;
    if (h12 === 0) h12 = 12;
    return String(h12);
  })();

  const [h12, setH12] = useState(initialH12);
  const [ap, setAp] = useState(initialAp);
  const [h24, setH24] = useState(parsed.h !== "" ? String(parsed.h) : "");
  const [m, setM] = useState(parsed.m);

  // keep in sync if parent value changes externally
  useEffect(() => {
    const p = parseValue(value);
    const nAp = p.h !== "" && p.h >= 12 ? "PM" : "AM";
    let nH12 = "";
    if (p.h !== "") {
      const t = p.h % 12;
      nH12 = String(t === 0 ? 12 : t);
    }
    // avoid state overwrite loops
    setH12((cur) => (cur === nH12 ? cur : nH12));
    setAp((cur) => (cur === nAp ? cur : nAp));
    setH24((cur) =>
      cur === (p.h === "" ? "" : String(p.h)) ? cur : p.h === "" ? "" : String(p.h)
    );
    setM((cur) => (cur === p.m ? cur : p.m));
  }, [value]);

  const emit12 = (next) => onChange(build12(next.h12, next.m, next.ap));
  const emit24 = (next) => onChange(build24(next.h24, next.m));

  const set12 = (patch) => {
    const next = { h12, m, ap, ...patch };
    if (patch.h12 !== undefined) setH12(patch.h12);
    if (patch.m !== undefined) setM(patch.m);
    if (patch.ap !== undefined) setAp(patch.ap);
    emit12(next);
  };
  const set24 = (patch) => {
    const next = { h24, m, ...patch };
    if (patch.h24 !== undefined) setH24(patch.h24);
    if (patch.m !== undefined) setM(patch.m);
    emit24(next);
  };

  const minutes = Array.from({ length: 60 }, (_, i) => i);

  return (
    <div className="space-y-2" data-testid={testId}>
      {/* Format toggle (hidden when parent controls format) */}
      {showToggle && (
        <div
          className="inline-flex rounded-full border overflow-hidden text-xs font-sans-ui"
          style={{ borderColor: "rgba(212,166,74,0.45)" }}
        >
          {["12", "24"].map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => setFmt(mode)}
              className={`px-3 py-1 transition ${
                fmt === mode
                  ? "bg-[color:var(--gold)] text-[color:var(--maroon-darker)] font-semibold"
                  : "text-[color:var(--gold-soft)]"
              }`}
              data-testid={`${testId}-fmt-${mode}`}
              aria-pressed={fmt === mode}
            >
              {mode === "12" ? "12H · AM/PM" : "24H"}
            </button>
          ))}
        </div>
      )}

      {fmt === "12" ? (
        <div className="grid grid-cols-3 gap-2">
          <select
            className="select"
            value={h12}
            onChange={(e) => set12({ h12: e.target.value })}
            required={required}
            data-testid={`${testId}-hour`}
          >
            <option value="">HH</option>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
              <option key={h} value={String(h)}>
                {String(h).padStart(2, "0")}
              </option>
            ))}
          </select>
          <select
            className="select"
            value={m}
            onChange={(e) => set12({ m: e.target.value })}
            required={required}
            data-testid={`${testId}-minute`}
          >
            <option value="">MM</option>
            {minutes.map((mm) => (
              <option key={mm} value={String(mm).padStart(2, "0")}>
                {String(mm).padStart(2, "0")}
              </option>
            ))}
          </select>
          <select
            className="select"
            value={ap}
            onChange={(e) => set12({ ap: e.target.value })}
            data-testid={`${testId}-ampm`}
          >
            <option value="AM">AM</option>
            <option value="PM">PM</option>
          </select>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <select
            className="select"
            value={h24}
            onChange={(e) => set24({ h24: e.target.value })}
            required={required}
            data-testid={`${testId}-hour24`}
          >
            <option value="">HH</option>
            {Array.from({ length: 24 }, (_, i) => i).map((h) => (
              <option key={h} value={String(h)}>
                {String(h).padStart(2, "0")}
              </option>
            ))}
          </select>
          <select
            className="select"
            value={m}
            onChange={(e) => set24({ m: e.target.value })}
            required={required}
            data-testid={`${testId}-minute24`}
          >
            <option value="">MM</option>
            {minutes.map((mm) => (
              <option key={mm} value={String(mm).padStart(2, "0")}>
                {String(mm).padStart(2, "0")}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
